/**
 * Admin settings and system utilities
 * Follows the unified UX pattern with stats, quick actions, and modern design
 */

import { useState, useMemo } from 'react';
import { adminApi } from '../../services/api';
import { clearStaticCache } from '../../services/staticContent';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import { QuickActionsGrid, QuickAction } from '../common/QuickActionsGrid';
import {
  Settings as SettingsIcon,
  RefreshCw,
  Database,
  Server,
  Globe,
  Calendar,
  Zap,
  HardDrive,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  HelpCircle,
  Lightbulb,
  FileJson,
  Activity,
} from 'lucide-react';

export default function Settings(): JSX.Element {
  const [regenerating, setRegenerating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lastRegenerated, setLastRegenerated] = useState<string | null>(null);

  const handleRegenerateStaticContent = async () => {
    if (!confirm('Regenerate all static content files? This will update categories, products, and featured items.')) {
      return;
    }

    setRegenerating(true);
    setMessage(null);

    try {
      const result = await adminApi.regenerateStaticContent();

      // Clear local cache to fetch fresh data
      clearStaticCache();

      setLastRegenerated(new Date().toISOString());
      setMessage({
        type: 'success',
        text: result.message || 'Static content regenerated successfully',
      });

      // Auto-hide success message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      console.error('Failed to regenerate static content:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to regenerate static content',
      });
    } finally {
      setRegenerating(false);
    }
  };

  // System info stats
  const systemStats: StatItem[] = useMemo(() => [
    {
      id: 'environment',
      label: 'Environment',
      value: import.meta.env.MODE === 'production' ? 'Production' : 'Development',
      icon: Server,
      variant: import.meta.env.MODE === 'production' ? 'success' : 'warning',
    },
    {
      id: 'version',
      label: 'Build Date',
      value: new Date().toLocaleDateString(),
      icon: Calendar,
      variant: 'default',
    },
    {
      id: 'status',
      label: 'System Status',
      value: 'Operational',
      icon: Activity,
      variant: 'success',
    },
  ], []);

  // Quick actions for settings
  const quickActions: QuickAction[] = useMemo(() => [
    {
      id: 'regenerate',
      title: 'Regenerate Content',
      description: 'Rebuild all static JSON files',
      icon: 'refresh-cw',
      onClick: handleRegenerateStaticContent,
      buttonText: regenerating ? 'Regenerating...' : 'Regenerate',
      variant: 'primary',
    },
    {
      id: 'clear-cache',
      title: 'Clear Local Cache',
      description: 'Clear browser cached data',
      icon: 'hard-drive',
      onClick: () => {
        clearStaticCache();
        setMessage({ type: 'success', text: 'Local cache cleared successfully' });
        setTimeout(() => setMessage(null), 3000);
      },
      buttonText: 'Clear Cache',
      variant: 'default',
    },
  ], [regenerating]);

  // Trigger items for display
  const triggers = [
    { event: 'Product created', icon: CheckCircle, color: 'text-green-500' },
    { event: 'Product updated', icon: RefreshCw, color: 'text-blue-500' },
    { event: 'Product deleted', icon: AlertTriangle, color: 'text-amber-500' },
    { event: 'Daily schedule (2 AM UTC)', icon: Clock, color: 'text-purple-500' },
  ];

  // Performance tips
  const tips = [
    {
      title: 'Static content caching',
      description: 'Homepage and products use GCS-hosted JSON files instead of direct Firestore queries, reducing costs by ~90%.',
      icon: Database,
    },
    {
      title: 'Automatic updates',
      description: 'When you publish/unpublish a product, static files regenerate automatically within seconds via Firestore triggers.',
      icon: Zap,
    },
    {
      title: 'CDN caching',
      description: 'Static files have appropriate cache headers for optimal CDN performance (24h for categories, 1h for products).',
      icon: Globe,
    },
    {
      title: 'Fallback mechanism',
      description: 'If static files are unavailable, the app gracefully falls back to hardcoded categories.',
      icon: HardDrive,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Settings
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            System configuration and utilities
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <SettingsIcon size={16} />
          <span>Admin Settings</span>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`p-4 rounded-xl flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
          )}
          <div>
            <p className="font-medium">{message.text}</p>
            {lastRegenerated && message.type === 'success' && (
              <p className="text-sm opacity-75 mt-1">
                Last regenerated: {new Date(lastRegenerated).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* System Status */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          System Status
        </h3>
        <StatsWidget stats={systemStats} columns={3} />
      </section>

      {/* Quick Actions */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Quick Actions
        </h3>
        <QuickActionsGrid actions={quickActions} columns={2} />
      </section>

      {/* Static Content Generation */}
      <section className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <FileJson size={20} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Static Content Generation
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Optimize Firestore costs with pre-generated JSON files
            </p>
          </div>
        </div>

        {/* Cost Optimization Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">
                Cost Optimization
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Static files reduce Firestore reads by ~90% for public pages (homepage, products catalog).
                Files are cached in GCS and automatically updated on product changes.
              </p>
            </div>
          </div>
        </div>

        {/* Automatic Triggers */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Automatic Regeneration Triggers
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {triggers.map((trigger, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <trigger.icon size={16} className={trigger.color} />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {trigger.event}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Regenerate Button */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleRegenerateStaticContent}
            disabled={regenerating}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <RefreshCw size={16} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Regenerating...' : 'Regenerate Static Content'}
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            This may take up to 30 seconds depending on the number of products
          </p>
        </div>
      </section>

      {/* System Information */}
      <section className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <Server size={20} className="text-gray-600 dark:text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            System Information
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Server size={14} className="text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Environment</p>
            </div>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {import.meta.env.MODE === 'production' ? 'Production' : 'Development'}
            </p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Globe size={14} className="text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">API Base URL</p>
            </div>
            <p className="font-medium text-gray-900 dark:text-gray-100 font-mono text-xs truncate">
              {import.meta.env.VITE_API_URL || 'Default'}
            </p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Database size={14} className="text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Static Content URL</p>
            </div>
            <p className="font-medium text-gray-900 dark:text-gray-100 font-mono text-xs truncate">
              {import.meta.env.VITE_STATIC_CONTENT_URL || 'Default GCS Bucket'}
            </p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Calendar size={14} className="text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Build Version</p>
            </div>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {new Date().toISOString().split('T')[0]}
            </p>
          </div>
        </div>
      </section>

      {/* Performance Tips */}
      <section className="card p-6 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 border-2 border-amber-200 dark:border-amber-800/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Lightbulb size={20} className="text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Performance Tips
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tips.map((tip, index) => (
            <div
              key={index}
              className="p-4 bg-white/60 dark:bg-gray-800/60 rounded-lg"
            >
              <div className="flex items-start gap-3">
                <tip.icon size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                    {tip.title}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {tip.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help with system settings?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn about configuration options and best practices
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}
